// BONK ROYALE (§4.1): one life, last bean (or team) standing. Places come from KO order (the edge
// phase assigns them, with ties for same-tick KOs); falls in progress resolve before the round
// ends (buzzer-beater); at the cap the survivors draw. Also raises MATCH POINT in the Golden Round
// (§4.4). Hot Potato reuses the win check and results (one life, same points).
import { NO_SLOT, TEAM_RESULT } from '../constants';
import { EV, KoFlag, emit } from '../events';
import { BS, ModeId } from '../ids';
import type { RoundClock } from '../maps/clock';
import type { Bean, World } from '../types';
import type { ModeRules, RoundResult } from './modeTypes';

function teamsLeft(mask: number): number {
  let n = 0;
  for (let m = mask; m !== 0; m &= m - 1) n++;
  return n;
}

/** KO events of the current tick (the deciding tick when the round ends). */
function kosThisTick(w: World): number {
  let n = 0;
  for (const e of w.events) if (e.type === EV.KO && e.tick === w.tick) n++;
  return n;
}

/** FINAL on every KO of the deciding tick, DOUBLE when two or more fell together and nobody is left. */
function flagFinalKOs(w: World, double: boolean): void {
  const flags = KoFlag.FINAL | (double ? KoFlag.DOUBLE : 0);
  for (const e of w.events) if (e.type === EV.KO && e.tick === w.tick) e.d |= flags;
}

function endRound(w: World, winner: number, team: number, double: boolean): void {
  w.over = true;
  w.winner = winner;
  for (const b of w.beans) if (b.active && !b.out) b.place = 1;
  flagFinalKOs(w, double);
  emit(w, EV.ROUND_OVER, winner, team);
}

/** The bean KO'd on the deciding tick when it is the only one (the last bean to fall wins). */
function soleLastKo(w: World): number {
  let slot = NO_SLOT;
  for (const e of w.events) {
    if (e.type !== EV.KO || e.tick !== w.tick) continue;
    if (slot !== NO_SLOT) return NO_SLOT;
    slot = e.a;
  }
  return slot;
}

/**
 * The §4.1 win check (server): once no bean is FALLING, the round ends when at most one bean (or
 * one team) is still in play, or at the cap (a DRAW between the survivors). When nobody is left,
 * the beans that fell on the deciding tick share 1st (DOUBLE BONK!); a single last faller wins.
 * MATCH POINT is raised once in the Golden Round when two beans remain.
 */
export function royaleTick(w: World, clock: RoundClock, predict: boolean): void {
  if (predict || w.over || w.tick < 0) return;
  let inPlay = 0;
  let falling = 0;
  let last = NO_SLOT;
  let teamMask = 0;
  for (const b of w.beans) {
    if (!b.active || b.out) continue;
    if (b.state === BS.FALLING) {
      falling++;
    } else {
      inPlay++;
      last = b.slot;
      teamMask |= 1 << b.team;
    }
  }
  if (w.cfg.golden && !w.mode.matchPoint && inPlay + falling === 2 && w.mode.aliveAtStart > 2) {
    w.mode.matchPoint = true;
    emit(w, EV.MATCH_POINT);
  }
  if (falling > 0) return;
  const teamsDecided = w.cfg.teamsOn && inPlay > 0 && teamsLeft(teamMask) === 1;
  if (inPlay > 1 && !teamsDecided && clock.tick < clock.cap) return;

  if (inPlay === 0) {
    const lastKo = soleLastKo(w);
    const team = lastKo === NO_SLOT ? 0 : w.beans[lastKo].team;
    endRound(w, lastKo, team, lastKo === NO_SLOT && kosThisTick(w) >= 2);
  } else if (teamsDecided) {
    endRound(w, TEAM_RESULT, w.beans[last].team, false);
  } else if (inPlay === 1) {
    endRound(w, last, w.beans[last].team, false);
  } else {
    endRound(w, NO_SLOT, 0, false);
  }
}

/** KOs credited to `slot`, split into regular and ghost KOs (credited while `slot` was already out). */
function creditedKOs(w: World, credit: Bean): { kos: number; ghostKos: number } {
  let kos = 0;
  let ghostKos = 0;
  for (const v of w.beans) {
    if (!v.active || !v.out || v.koBy !== credit.slot || v.slot === credit.slot) continue;
    if (w.cfg.teamsOn && v.team !== 0 && v.team === credit.team) continue;
    if (credit.out && credit.koTick < v.koTick) ghostKos++;
    else kos++;
  }
  return { kos, ghostKos };
}

/**
 * Round results (§19.3) for one-life modes: survivors place 1 (and draw at the cap), the rest keep
 * their KO place; KOs, ghost KOs and the bounty from the victims' credits; the team result.
 */
export function oneLifeResults(w: World): RoundResult[] {
  let survivors = 0;
  for (const b of w.beans) if (b.active && !b.out) survivors++;
  const winTeam = w.winner === TEAM_RESULT ? w.beans.find((b) => b.active && !b.out)?.team ?? 0 : 0;
  const draw = w.winner === NO_SLOT && survivors > 1;
  const crowned = w.cfg.crownedSlot === NO_SLOT ? null : w.beans[w.cfg.crownedSlot];
  const out: RoundResult[] = [];
  for (const b of w.beans) {
    if (!b.active) continue;
    const { kos, ghostKos } = creditedKOs(w, b);
    out.push({
      slot: b.slot,
      place: b.out ? b.place : 1,
      kos,
      ghostKos,
      bounty: crowned !== null && crowned.out && crowned.koBy === b.slot && crowned !== b && !(w.cfg.teamsOn && crowned.team === b.team),
      crownTicks: b.crownTicks,
      survived: !b.out,
      draw: draw && !b.out,
      team: b.team,
      teamWon: winTeam !== 0 && b.team === winTeam,
    });
  }
  return out;
}

export function countActive(w: World): number {
  let n = 0;
  for (const b of w.beans) if (b.active) n++;
  return n;
}

export const ROYALE: ModeRules = {
  id: ModeId.ROYALE,
  name: 'Bonk Royale',
  minBeans: 2,
  respawns: false,
  ghosts: true,
  init(w) {
    w.mode.aliveAtStart = countActive(w);
  },
  onTick: royaleTick,
  onKO() {},
  onHit() {},
  onTouch() {},
  speedMult() {
    return 1;
  },
  itemsBlocked() {
    return false;
  },
  results: oneLifeResults,
};
